<script setup>
import {computed, nextTick, onBeforeUnmount, onMounted, reactive, ref} from "vue";
import router from "@/router/index.js";
import {listBizCategoryTree} from "@/api/bizCategory/bizCategory.js";
import {singleQrcodePrizeCodes} from "@/api/prizeCodes/prizeCodes.js";
import {listGoodsCards} from "@/api/goodsCards/goodsCards.js"
import {listBicycleDeviceCodes} from "@/api/bicycleDeviceCodes/bicycleDeviceCodes.js"
import {debounceFn} from "@/hooks/debounce.js";
import moment from "moment";

// 搜索表单数据
const searchForm = reactive({
  dateType: "ALL", // ALL: 全部日期, RANGE: 日期区间
  startDate: null,
  endDate: null,
  cardCode: "",
  prizeName: "",
  prizeCategory: [],
  cardStatus: "",
})

// 卡片状态选项
const cardStatusOptions = [
  { label: "已创建", value: "CREATED" },
]

// 奖品类目数据源
const prizeCategoryOptions = ref([])
// 奖品类目树配置
const categoryProps = {
  value: "bizCategoryId",
  label: "bizCategoryName",
  children: "children"
}
// 获取奖品类目树
const handleGetPrizeCategory = async () => {
  try {
    prizeCategoryOptions.value = await listBizCategoryTree()
  } catch (e) {
    console.log(e)
  }
}


/*表格数据展示*/
// 表格加载状态
const loading = ref(false)
// 表格锚点
const tableRef = ref()
// 表格滚动容器
let tableScrollWrapper = null
// 滚动加载状态
const loadMoreLoading = ref(false)
// 当前页码
const page = ref(1)
// 单页条数
const pageSize = 20
// 是否还有下一页
const hasNextPage = ref(false)

// 表格headers
const headers = [
  { title: "卡片编号", key: "goodsCardNo", align: "center", nowrap: true },
  { title: "申请日期", key: "applyTime", align: "center", nowrap: true },
  { title: "创建人", key: "crNickname", align: "center", nowrap: true },
  { title: "卡片电子图片", key: "goodsCardImage", align: "center", nowrap: true },
  { title: "卡片二维码", key: "barCode", align: "center", nowrap: true },
  { title: "绑定奖品", key: "prizeName", align: "center", nowrap: true },
  { title: "奖品类目", key: "bizCategoryName", align: "center", nowrap: true },
  { title: "状态", key: "status", align: "center", nowrap: true },
  { title: "操作栏", key: "controls", align: "center", nowrap: true },
]

// 已加载表格数据
const tableData = ref([])

// 表格展示高度
const tableHeaderHeight = 56
const tableRowHeight = 56
const tableHeight = computed(() => {
  const visibleRows = Math.min(Math.max(tableData.value.length, 4), 8)
  return tableHeaderHeight + visibleRows * tableRowHeight
})

/*表格多选*/
const selectedRows = ref([])
const handleSelectionChange = (selection) => {
  selectedRows.value = selection
}

/*过滤器变化触发搜索（节流）*/
const debouncedSearch = debounceFn(() => {
  getCardList(true)
}, 400)

const handleFilterChange = () => {
  debouncedSearch()
}

/*触底翻页*/
// 加载下一页
const loadNextPage = () => {
  getCardList(false)
}

// 处理表格滚动触底
const handleTableScroll = (e) => {
  const target = e.target
  if (target.scrollHeight - target.scrollTop - target.clientHeight > 24) return
  loadNextPage()
}

// 绑定表格滚动事件
const bindTableScroll = async () => {
  await nextTick()
  unbindTableScroll()
  tableScrollWrapper = tableRef.value?.$el?.querySelector(".el-table__body-wrapper")
  tableScrollWrapper?.addEventListener("scroll", handleTableScroll, { passive: true })
}

// 解绑表格滚动事件
const unbindTableScroll = () => {
  tableScrollWrapper?.removeEventListener("scroll", handleTableScroll)
  tableScrollWrapper = null
}

// 获取卡片列表
const getCardList = async (isReset = false) => {
  if (!isReset && (!hasNextPage.value || loadMoreLoading.value)) return
  try {
    if (isReset) {
      page.value = 1
      tableData.value = []
      hasNextPage.value = false
      loading.value = true
      await nextTick()
    } else {
      loadMoreLoading.value = true
    }
    const pageData = {
      page: page.value,
      pageSize,
    }
    const param = {
      cardCode: searchForm.cardCode || null,
      prizeName: searchForm.prizeName || null,
      startDate: searchForm.dateType === "RANGE" && searchForm.startDate ? moment(searchForm.startDate).format("YYYY-MM-DD") : null,
      endDate: searchForm.dateType === "RANGE" && searchForm.endDate ? moment(searchForm.endDate).format("YYYY-MM-DD") : null,
    }
    const res = await listGoodsCards(pageData, param)
    const list = res.list || []
    tableData.value = isReset ? list : tableData.value.concat(list)
    hasNextPage.value = Boolean(res.hasNextPage)
    if (res.hasNextPage) page.value += 1
  } catch (e) {
    console.log(e)
  } finally {
    loading.value = false
    loadMoreLoading.value = false
  }
}

// 处理搜索
const handleSearch = () => {
  getCardList(true)
}

onMounted(() => {
  bindTableScroll()
  getCardList(true)
  handleGetPrizeCategory()
})

onBeforeUnmount(() => {
  unbindTableScroll()
})

/*二维码展示弹窗*/
const openQrCodeDialog = ref(false)
const currentQrCodeItem = ref(null)
const qrCodeImageUrl = ref("")
const qrCodeLoading = ref(false)
const handleShowQrCode = async (row) => {
  currentQrCodeItem.value = row
  openQrCodeDialog.value = true
  qrCodeImageUrl.value = ""
  if (!row.barCode) return
  try {
    qrCodeLoading.value = true
    const res = await singleQrcodePrizeCodes({ barCode: row.barCode })
    qrCodeImageUrl.value = URL.createObjectURL(res.data)
  } catch (e) {
    console.log(e)
  } finally {
    qrCodeLoading.value = false
  }
}
const handleDownloadQrCode = async () => {
  const barCode = currentQrCodeItem.value?.barCode
  if (!barCode) return
  try {
    const res = await singleQrcodePrizeCodes({ barCode })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(res.data)
    link.setAttribute("download", `${barCode}.png`)
    document.body.appendChild(link)
    link.click()
    setTimeout(() => {
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    }, 100)
  } catch (e) {
    console.log(e)
  }
}

/*创建寻宝卡*/
// 暂无可用的未绑定卡片二维码弹窗
const openNoQrCodeDialog = ref(false)
// 处理创建寻宝卡
const handleCreateQRCode = async () => {
  try {
    const res = await listBicycleDeviceCodes({ page: 1, pageSize: 1 }, { bound: false })
    console.log(res.list)
    const list = res.list || []
    if (list.length === 0) {
      openNoQrCodeDialog.value = true
    } else {
      router.push("/addTreasureActivityCard")
    }
  } catch (e) {
    console.log(e)
    openNoQrCodeDialog.value = true
  }
}
// 处理前往生成二维码
const handleGoGenerateQrCode = () => {
  openNoQrCodeDialog.value = false
  router.push("/listCardQRCode")
}
// 查看寻宝卡详情
const handleViewDetail = (row) => {
  router.push({ path: "/addTreasureActivityCard", query: { goodsCardId: row.goodsCardId } })
}
</script>

<template>
  <div class="container_box mg_b_2 qr-toolbar" style="padding: 0;">
    <div class="flex_bt" style="padding: 16px;">
      <h2>寻宝卡</h2>
      <v-btn color="success" @click="handleCreateQRCode">
        <template v-slot:default>
          <div class="flex_bt">
            <img
                style="width: 14px; height: 14px; margin-right: 4px"
                src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/filter/filter/plus.svg"
            />
            <span>创建寻宝卡</span>
          </div>
        </template>
      </v-btn>
    </div>
    <div class="flex_left" style="padding: 20px;background-color:#f5f5f8;">
      <v-btn color="success">最近申请创建的寻宝卡</v-btn>
    </div>
    <div class="filter-section">
      <el-form :model="searchForm" class="filter-form" label-position="left">
        <div class="filter-row">
          <!-- 日期筛选 -->
          <div class="filter-item filter-date">
            <el-radio-group v-model="searchForm.dateType" @change="handleFilterChange">
              <el-radio value="ALL">全部日期</el-radio>
              <el-radio value="RANGE">
                <span class="date-range-label">从:</span>
                <el-date-picker
                    v-model="searchForm.startDate"
                    type="date"
                    placeholder="开始日期"
                    class="date-picker-sm"
                    :disabled="searchForm.dateType !== 'RANGE'"
                    @change="() => { if (searchForm.startDate && searchForm.endDate && new Date(searchForm.endDate) < new Date(searchForm.startDate)) searchForm.endDate = null; handleFilterChange() }"
                />
                <span class="date-range-label">到:</span>
                <el-date-picker
                    v-model="searchForm.endDate"
                    type="date"
                    placeholder="结束日期"
                    class="date-picker-sm"
                    :disabled="searchForm.dateType !== 'RANGE'"
                    :disabled-date="(time) => searchForm.startDate ? time.getTime() < new Date(searchForm.startDate).getTime() : false"
                    @change="handleFilterChange"
                />
              </el-radio>
            </el-radio-group>
          </div>

          <!-- 卡片编号 -->
          <el-form-item class="filter-item">
            <el-input
                v-model="searchForm.cardCode"
                placeholder="卡片编号"
                clearable
                class="filter-input"
                @input="handleFilterChange"
            />
          </el-form-item>

          <!-- 绑定奖品名称 -->
          <el-form-item class="filter-item">
            <el-input
                v-model="searchForm.prizeName"
                placeholder="绑定奖品名称"
                clearable
                class="filter-input"
                @input="handleFilterChange"
            />
          </el-form-item>

          <!-- 奖品类目 -->
          <el-form-item class="filter-item">
            <el-cascader
                v-model="searchForm.prizeCategory"
                :options="prizeCategoryOptions"
                :props="categoryProps"
                placeholder="奖品类目"
                clearable
                class="filter-input"
                @change="handleFilterChange"
            />
          </el-form-item>

          <!-- 卡片状态 -->
          <el-form-item class="filter-item">
            <el-select
                v-model="searchForm.cardStatus"
                placeholder="卡片状态"
                clearable
                class="filter-input"
                @change="handleFilterChange"
                :disabled="true"
            >
              <el-option
                  v-for="item in cardStatusOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
              />
            </el-select>
          </el-form-item>
        </div>
      </el-form>
    </div>
  </div>

  <div class="container_box card-table-box">
    <el-table
        ref="tableRef"
        :data="tableData"
        v-loading="loading"
        border
        stripe
        style="width: 100%"
        :max-height="tableHeight"
        @selection-change="handleSelectionChange"
        @scroll="handleTableScroll"
    >
      <el-table-column type="selection" width="50" align="center" />
      <el-table-column prop="goodsCardNo" label="卡片编号" align="center" />
      <el-table-column prop="applyTime" label="申请日期" align="center" />
      <el-table-column prop="crNickname" label="创建人" align="center" />
      <el-table-column label="卡片电子图片" align="center">
        <template #default="{ row }">
          <el-image
              style="width: 60px; height: 60px"
              :src="row.goodsCardImage || ''"
              fit="cover"
          >
            <template #error>
              <div class="image-placeholder">暂无图片</div>
            </template>
          </el-image>
        </template>
      </el-table-column>
      <el-table-column label="卡片二维码" align="center">
        <template #default="{ row }">
          <div class="flex_center" style="cursor: pointer; gap: 4px;" @click="handleShowQrCode(row)">
            <el-text type="primary">{{ row.barCode }}</el-text>
            <v-icon color="#409eff" size="small">mdi-qrcode</v-icon>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="绑定奖品" align="center">
        <template #default="{ row }">
          {{ row.goodsName }}{{ row.properties?.length ? ' / ' + row.properties.map(p => p.propertyName).join(' / ') : '' }}
        </template>
      </el-table-column>
      <el-table-column prop="bizCategoryName" label="奖品类目" align="center" />
      <el-table-column label="状态" align="center">
        <template #default>
          已创建
        </template>
      </el-table-column>
      <el-table-column label="操作栏" align="center">
        <template #default="{ row }">
          <el-link type="primary" :underline="false" @click="handleViewDetail(row)">查看</el-link>
        </template>
      </el-table-column>

      <template #append>
        <div class="load-more-row">
          <span v-if="loadMoreLoading" class="load-more-text">加载中...</span>
          <span v-else-if="!hasNextPage && tableData.length > 0" class="load-more-text">没有更多数据了</span>
        </div>
      </template>
    </el-table>
  </div>

  <el-dialog
      v-model="openQrCodeDialog"
      width="480"
      title="寻宝卡二维码"
      :close-on-click-modal="false"
  >
    <div style="text-align: center; padding: 20px 0;">
      <div style="font-size: 18px; font-weight: 600; color: #272539; margin-bottom: 16px;">{{ currentQrCodeItem?.barCode || currentQrCodeItem?.cardCode }}</div>
      <div style="margin-bottom: 20px; display: flex; justify-content: center;">
        <img v-if="qrCodeImageUrl" :src="qrCodeImageUrl" style="width: 160px; height: 160px;" />
        <span v-else-if="qrCodeLoading" style="color: #999;">加载中...</span>
        <span v-else style="color: #ccc;">暂无二维码</span>
      </div>
      <v-btn color="primary" variant="elevated" @click="handleDownloadQrCode">二维码下载</v-btn>
    </div>
  </el-dialog>

  <el-dialog
      v-model="openNoQrCodeDialog"
      width="480"
      :close-on-click-modal="false"
      :show-close="true"
  >
    <template #header>
      <div class="yt_dialog_title">温馨提示</div>
    </template>
    <div style="padding: 20px 33px; font-size: 16px; color: #272539; line-height: 1.6; text-align: left;">
      目前暂无可用的未绑定卡片二维码，无法创建。请先创建生成卡片二维码
    </div>
    <template #footer>
      <div class="yt_dialog_footer">
        <v-btn variant="elevated" @click="openNoQrCodeDialog = false">取消</v-btn>
        <v-btn class="mg_l_2" color="primary" variant="elevated" @click="handleGoGenerateQrCode">确认，并前往生成</v-btn>
      </div>
    </template>
  </el-dialog>

</template>

<style lang="scss" scoped>
.filter-section {
  padding: 20px;
  background: #fff;
}

.filter-form {
  width: 100%;
}

.filter-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.filter-item {
  margin-bottom: 0;
}

.filter-input {
  width: 200px;
}

.filter-date {
  :deep(.el-radio) {
    display: flex;
    align-items: center;
    white-space: nowrap;
  }
  :deep(.el-radio__label) {
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

.date-range-label {
  margin: 0 4px;
  font-size: 14px;
  color: #606266;
}

.date-picker-sm {
  width: 140px;
}

.card-table-box {
  margin-top: 16px;
  padding: 16px;
}

.image-placeholder {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  color: #999;
  font-size: 12px;
}

:deep(.el-table__header-wrapper th) {
  background: #f5f5f8 !important;
}

.load-more-row {
  width: 100%;
  text-align: center;
  padding: 12px 0;
}

.load-more-text {
  font-size: 14px;
  color: #999;
}


</style>

<style>
.el-dialog__header {
  text-align: center !important;
  padding-left: 20px !important;
  padding-right: 20px !important;
}
</style>
